import { useMemo } from 'react';
import dayjs from 'dayjs';
import { assigneeName, shortName } from '../../queries/scheduling';
import { P } from '../../theme/tokens';
import { AGENT_COLORS, STATUS, TYPE_LABEL, UNASSIGNED } from './constants';
import { dateIn, timeIn } from './time';

const SUNDAY = 0;

const toEvent = (b, tz) => ({
  id: b._id,
  rowId: b.assignedTo?._id || UNASSIGNED.id,
  date: dateIn(b.start, tz),
  time: timeIn(b.start, tz),
  title: b.type === 'block' ? b.notes || 'Blocked' : b.contact?.name || 'Unnamed contact',
  detail: TYPE_LABEL[b.type] || 'Tour',
  statusLabel: (STATUS[b.status] || STATUS.booked).label,
  muted: b.status !== 'booked',
  struck: b.status === 'cancelled'
});

export function useCalendarModel({ bookings, assignees, days, weeklyHours, tz }) {
  const colorById = useMemo(
    () => new Map(assignees.map((a, i) => [a._id, AGENT_COLORS[i % AGENT_COLORS.length]])),
    [assignees]
  );

  return useMemo(() => {
    const events = bookings.map((b) => toEvent(b, tz));
    const people = new Map();
    bookings.forEach(({ assignedTo: a }) => a && people.set(a._id, a));
    const rows = [...people.values()]
      .map((a) => ({ id: a._id, label: shortName(a), title: assigneeName(a), color: colorById.get(a._id) || P.muted }))
      .sort((x, y) => x.title.localeCompare(y.title));
    if (events.some((e) => e.rowId === UNASSIGNED.id)) rows.push(UNASSIGNED);

    const sundayOpen = Boolean(weeklyHours?.[SUNDAY]?.length) || events.some((e) => dayjs(e.date).day() === SUNDAY);
    const visibleDays = sundayOpen ? days : days.filter((d) => dayjs(d).day() !== SUNDAY);

    return { events, rows, days: visibleDays };
  }, [bookings, colorById, days, weeklyHours, tz]);
}
